import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Field, FieldArray, Form, Formik, FormikErrors } from 'formik';
import { HeadingOne, HeadingTwo, HelpButton, ScrollTop } from '../../../common/components';
import { useWizardEffect } from '../../../common/hooks';
import { HeadingLayout } from '../../../common/layout';
import {
  Application,
  ApplicationAction,
  ApplicationActionType,
  Catalogue,
  CatalogueItem,
  DataStatus,
  PriceType,
  SolutionType,
} from '../../../common/types';
import { ApplicationContext } from '../../context/application-context';
import { PRODUCT_DETAILS_EXTRA } from '../../tech-debt';
import { FullPageLoader, ShowIf, TapOnPhoneModal } from '../../../../components';
import { getChooseSolutionHeadings } from 'src/features/order/helpers/headings';
import { Carousel, CarouselCard, ProductCard } from '../../components';
import { EventName, HelpButtonAction, PageName } from '../../services/order-tealium-services';
import { BasketInput } from '../../components/basket-input/basket-input';
import { QuantityUpdateConfirmModal } from '../../components/quantity-update-confirm-modal/quantity-update-confirm-modal';
import { getSolutionsDeliveryBreakdown } from '../../helpers/delivery-helpers';
import { FormikChangeListener } from '../../components/product-quantity-change-listener/product-quantity-change-listener';
import { Toast, notify } from 'src/features/common/components/toast/toast';
import { formatCurrencyAmount } from '../../../common/helpers/currency';
import { DEVICE_NAMES } from '../../tech-debt/product-details-extra';
import { useQueryConfig } from '../../../common/hooks/use-query-config';
import { QueryParams } from '../../types/query-params';
import { getCatalogueWithoutCategoryChildren } from '../../helpers/catalogue-filters';
import { convertToAutomationId } from 'src/helpers/functional-helpers';
import { urlQueryParams } from 'src/helpers/custom-url-search-param';
import { useIsMobile } from 'src/features/common/hooks/use-is-mobile';
import { getTotalProducts, isOnlyDeviceSelected } from 'src/features/common';
import FeesSliderModal from 'src/features/onboarding/components/fees-slider-modal/fees-slider-modal';
import MiniAppEvents from 'src/helpers/miniapp-events';
import 'react-toastify/dist/ReactToastify.css';
import './choose-solution.scss';

export type ChooseSolutionForm = {
  catalogue: Catalogue;
  isConfirmModalVisible?: boolean;
  catalogueItemTop: number;
};

export function ChooseSolution() {
  const { application, dispatch, catalogue, feesService, orderTealiumService } =
    useContext(ApplicationContext);
  const isMobile = useIsMobile();
  const [showTapOnPhoneModal, setShowTapOnPhoneModal] = React.useState(false);
  const queryConfig = useQueryConfig<QueryParams>();
  const queryParams = urlQueryParams(window.location.search);
  const getInitialCatalogueItemTopIndex = (catalogue: Catalogue, deviceName: string) => {
    return catalogue.products.find((product) => product.device.title === deviceName)?.index || 0;
  };
  const [showFeesSlider, setShowFeesSlider] = useState<boolean>(false);

  const headings = useMemo(
    () => getChooseSolutionHeadings(application.solutionType, application.agentJourney),
    [application.solutionType],
  );

  const inMiniApp = useMemo((): boolean => MiniAppEvents.isInMiniApp(), []);

  const validateQueryParams = (params: QueryParams) => {
    return (
      (params.sales_org && params.sales_agent) ||
      (!!params.highlight && Object.values(DEVICE_NAMES).includes(params.highlight))
    );
  };

  const addToCart = (productName: string) => {
    const product: CatalogueItem | undefined = catalogue.products.find(
      (product) => product.device.title === productName,
    );

    if (!product) {
      return;
    }

    dispatch({
      type: ApplicationActionType.UPDATE_BASKET_QUANTITY,
      productId: product.device.id,
      acquiringChannel: product.device.acquiringChannel,
      quantity: 1,
      catalogue: catalogue,
    });
  };

  const getInitialValues = (): ChooseSolutionForm => {
    if (validateQueryParams(queryParams)) {
      queryConfig.sales_agent = queryParams['sales_agent'];
      queryConfig.sales_org = queryParams['sales_org'];
      queryConfig.highlight = queryParams['highlight'] || DEVICE_NAMES.VODAPAY_ECOM_GATEWAY;
    }
    const catalogueFiltered = getCatalogueWithoutCategoryChildren(catalogue);
    const highlightDeviceTitle = inMiniApp
      ? DEVICE_NAMES.VODAPAY_MAX
      : queryConfig.highlight || DEVICE_NAMES.VODAPAY_KWIKA;

    return {
      catalogue: catalogueFiltered,
      catalogueItemTop: getInitialCatalogueItemTopIndex(catalogueFiltered, highlightDeviceTitle),
    };
  };

  useEffect(() => {
    if (queryConfig.highlight) {
      addToCart(queryConfig.highlight);
      return;
    }

    if (validateQueryParams(queryParams) && catalogue.statusProducts === DataStatus.LOADED) {
      if (queryConfig.sales_agent && queryConfig.sales_agent) {
        catalogue.products.map((product) => {
          if (product.device.title === DEVICE_NAMES.VODAPAY_ECOM_GATEWAY) {
            dispatch({
              type: ApplicationActionType.UPDATE_BASKET_QUANTITY,
              productId: product.device.id,
              acquiringChannel: product.device.acquiringChannel,
              quantity: 1,
              catalogue: catalogue,
            });
          }
        });
      }
    }
  }, [catalogue.statusProducts]);

  useEffect(() => {
    if (queryConfig.fees_slider) {
      setShowFeesSlider(true);
    }
    orderTealiumService?.onChooseSolutionPageStart();
  }, []);

  const { setNextButtonEnabled, setOnNextEventHandler } = useWizardEffect(
    () => ({
      nextButtonLabel:
        application.solutionType === SolutionType.buyInStoreFicaOnline && !application.agentJourney
          ? 'Activate my device'
          : 'Proceed to checkout',
      backButtonVisible: false,
      nextButtonEnabled:
        catalogue.statusAll === DataStatus.LOADED && !isAnyFormErrors(getInitialValues()),
      hideProgressBar:
        application.solutionType === SolutionType.buyInStoreFicaOnline && !application.agentJourney,
    }),
    [application, catalogue],
  );

  return (
    <section className="ChooseSolutionPage">
      <FullPageLoader showLoader={catalogue.statusProducts !== DataStatus.LOADED} />
      <ScrollTop />

      {showTapOnPhoneModal && <TapOnPhoneModal onClose={() => setShowTapOnPhoneModal(false)} />}

      <FeesSliderModal
        show={showFeesSlider}
        hide={() => setShowFeesSlider(false)}
        selectedDevices={[]}
        feesService={feesService}
        catalogue={catalogue}
      />

      <ShowIf condition={catalogue.statusProducts === DataStatus.LOADED}>
        <HeadingLayout
          renderMainHeading={() => <HeadingOne>{headings.main}</HeadingOne>}
          renderSubHeading={() => <HeadingTwo>{headings.sub}</HeadingTwo>}
        />

        <Formik<ChooseSolutionForm>
          innerRef={(formik) => formik && setOnNextEventHandler(formik.submitForm)}
          initialValues={getInitialValues()}
          validate={validate}
          onSubmit={submit}
          enableReinitialize={true}
        >
          {({ values, setFieldValue, submitForm }) => {
            // TODO: use device indicated in query parameters

            const setModalIsVisible = (isVisible: boolean) => {
              setFieldValue('isConfirmModalVisible', isVisible);
            };

            if (values.catalogue.products.length > 0) {
              const catalogueItemsBottom = values.catalogue.products;

              return (
                <Form>
                  <FormikChangeListener<ChooseSolutionForm>>
                    {(oldValues, newValues) => {
                      newValues.catalogue.products.forEach((product, index) => {
                        let oldValue = oldValues.catalogue.products[index].device.quantity;
                        let isBasket = false;

                        if (product.device.quantity === oldValue) {
                          return;
                        }
                        if (
                          product.device.quantity > oldValue ||
                          (product.device.quantity !== undefined && oldValue === undefined)
                        ) {
                          notify({
                            children: 'Product added to cart',
                            autoClose: 5000,
                          });

                          orderTealiumService?.onCartTypeAction(
                            isBasket,
                            PageName.CHOOSE_SOLUTION,
                            product.device.title,
                            EventName.CART_ADD,
                          );
                        } else if (product.device.quantity < oldValue) {
                          orderTealiumService?.onCartTypeAction(
                            isBasket,
                            PageName.CHOOSE_SOLUTION,
                            product.device.title,
                            EventName.CART_REMOVE,
                          );
                        }
                      });

                      if (
                        getTotalProducts(oldValues.catalogue) !==
                          oldValues.catalogue.maxProductsPerOrder &&
                        getTotalProducts(newValues.catalogue) ===
                          newValues.catalogue.maxProductsPerOrder
                      ) {
                        notify({
                          children: 'Maximum 70 products allowed per order',
                          autoClose: 5000,
                        });
                      }
                    }}
                  </FormikChangeListener>
                  <Toast />
                  <QuantityUpdateConfirmModal
                    isModalVisible={!!values.isConfirmModalVisible}
                    onModalShow={() => orderTealiumService?.onReduceQuantityModalStart()}
                    onCancel={() => {
                      setModalIsVisible(false);
                    }}
                    onConfirm={() =>
                      submitForm().then(() => {
                        setModalIsVisible(false);
                        orderTealiumService?.onReduceQuantityModalAction();
                      })
                    }
                  />
                  <Field
                    name="catalogue"
                    component={BasketInput}
                    onBasketOpened={(isBasketOpen) => {
                      if (isBasketOpen) {
                        orderTealiumService?.onCartViewed(PageName.CHOOSE_SOLUTION);
                      }
                    }}
                    automationid="Revamp_Choose_Solution_Basket"
                  />
                  <FieldArray
                    name="products"
                    render={() => (
                      <>
                        <section className="solution-area-large">
                          <ProductField
                            catalogueItem={values.catalogue.products[values.catalogueItemTop]}
                            catalogue={values.catalogue}
                            application={application}
                          />
                        </section>

                        {catalogueItemsBottom.length > 1 && (
                          <>
                            <HeadingLayout
                              renderMainHeading={() => (
                                <HeadingOne>Customers also bought this...</HeadingOne>
                              )}
                            />

                            <section className="solution-area-small">
                              <Carousel hasArrows={catalogueItemsBottom.length > 3 || isMobile}>
                                {catalogueItemsBottom.map((product) => (
                                  <div
                                    key={product.index}
                                    onClick={() =>
                                      setFieldValue('catalogueItemTop', product.index, false)
                                    }
                                  >
                                    <CarouselCardField
                                      catalogueItem={product}
                                      catalogue={values.catalogue}
                                    />
                                  </div>
                                ))}
                              </Carousel>
                            </section>
                          </>
                        )}
                      </>
                    )}
                  />
                </Form>
              );
            }
          }}
        </Formik>
      </ShowIf>
      <HelpButton
        onOpen={() =>
          orderTealiumService?.onHelpButtonActions(PageName.CHOOSE_SOLUTION, HelpButtonAction.CLICK)
        }
        onEmailClicked={() =>
          orderTealiumService?.onHelpButtonActions(PageName.CHOOSE_SOLUTION, HelpButtonAction.EMAIL)
        }
        onPhoneNumberClicked={() =>
          orderTealiumService?.onHelpButtonActions(PageName.CHOOSE_SOLUTION, HelpButtonAction.CALL)
        }
      />
    </section>
  );

  function isAnyFormErrors(values: ChooseSolutionForm) {
    const errors = validate(values);
    return Object.values(errors).length > 0;
  }

  function validate(values: ChooseSolutionForm) {
    const errors: FormikErrors<Catalogue> = {};

    const totalDevices = getTotalProducts(values.catalogue);

    if (totalDevices > values.catalogue.maxProductsPerOrder) {
      errors.products = 'total devices per order cannot exceed 70';
    }

    if (totalDevices === 0) {
      errors.products = 'total devices per order must be greater than zero';
    }

    setNextButtonEnabled(Object.values(errors).length === 0);
    return errors;
  }

  async function submit(values: ChooseSolutionForm, helpers) {
    const isOnlyTapOnPhone = isOnlyDeviceSelected(values.catalogue, DEVICE_NAMES.TAP_ON_PHONE);

    if (isOnlyTapOnPhone && application.solutionType !== SolutionType.thirdPartyOnboarding) {
      setShowTapOnPhoneModal(true);
      return;
    }

    const { solutionsWithTooManyAddresses } = getSolutionsDeliveryBreakdown(
      application,
      values.catalogue,
      true,
    );

    if (solutionsWithTooManyAddresses.length > 0 && !values.isConfirmModalVisible) {
      helpers.setFieldValue('isConfirmModalVisible', true);
      return;
    }

    values.catalogue.products.forEach((product) => {
      if (product.device.quantity !== undefined) {
        orderTealiumService?.onChooseSolutionEnd(product.device.title);
      }
    });

    const actions: ApplicationAction[] = values.catalogue.products
      .filter((product) => product.device.quantity !== product.device.quantityOld)
      .map((product) => {
        if (product.device.quantity === 0) {
          return {
            type: ApplicationActionType.REMOVE_BASKET_QUANTITY,
            productId: product.device.id,
            acquiringChannel: product.device.acquiringChannel,
            catalogue: catalogue,
          };
        } else {
          return {
            type: ApplicationActionType.UPDATE_BASKET_QUANTITY,
            productId: product.device.id,
            acquiringChannel: product.device.acquiringChannel,
            quantity: product.device.quantity,
            catalogue: catalogue,
          };
        }
      });

    const productItems = values.catalogue.products
      .filter((product) => product.device.quantity > 0)
      .map((product) => product);

    actions.push({ type: ApplicationActionType.SHOW_DEVICE_ACTIVATION, productItems });

    if (queryConfig.sales_org !== undefined && queryConfig.sales_agent !== undefined) {
      actions.push({
        type: ApplicationActionType.UPDATE_SALES_DETAILS,
        organisation: queryConfig.sales_org,
        agent: queryConfig.sales_agent,
      });
    }

    actions.push({ type: ApplicationActionType.NAVIGATE_TO_NEXT_STEP });
    dispatch({ type: ApplicationActionType.MULTIPLE_ACTIONS, actions });
  }
}

type ProductFieldProps = {
  catalogueItem: CatalogueItem;
  catalogue: Catalogue;
  application: Application;
};

function ProductField({ catalogueItem, catalogue, application }: ProductFieldProps) {
  const productStaticConfig = PRODUCT_DETAILS_EXTRA[catalogueItem.device.title];
  const device = catalogueItem.device;

  let price = '';
  let priceSuffix: string | undefined = undefined;

  switch (catalogueItem.price.priceType) {
    case PriceType.FREE:
      price = 'Free';
      break;
    case PriceType.ONCE_OFF:
      price = `R${formatCurrencyAmount(catalogueItem.price.price)}`;
      priceSuffix = 'once off';
      break;
    case PriceType.MONTHLY:
      price = `R${formatCurrencyAmount(catalogueItem.price.price)}`;
      priceSuffix = 'monthly';
      break;
  }

  const total = getTotalProducts(catalogue);
  const maximum =
    total >= catalogue.maxProductsPerOrder
      ? catalogueItem.device.quantity || 0
      : catalogueItem.device.maxQuantity ||
        catalogue.maxProductsPerOrder - (total - (catalogueItem.device.quantity || 0));

  return (
    <Field
      name={`catalogue.products[${catalogueItem.index}].device.quantity`}
      image={device.imageBase64}
      title={device.title}
      price={price}
      priceSuffix={priceSuffix}
      minimalDisplay={application.solutionType === SolutionType.buyInStoreFicaOnline}
      hasLoaded={catalogueItem.price.status === DataStatus.LOADED}
      points={productStaticConfig?.description || []}
      showMoreFeatures={device.featureTextList || []}
      icons={productStaticConfig?.icons}
      badge={productStaticConfig?.badge}
      component={ProductCard}
      min={device.minQuantity}
      max={maximum}
      automationid={`Revamp_Choose_Solution_Product_Card_${convertToAutomationId(device.title)}`}
    />
  );
}

type CarouselCardFieldProps = {
  catalogueItem: CatalogueItem;
  catalogue: Catalogue;
};

function CarouselCardField({ catalogueItem, catalogue }: CarouselCardFieldProps) {
  const productStaticConfig = PRODUCT_DETAILS_EXTRA[catalogueItem.device.title];

  let price = '';

  switch (catalogueItem.price.priceType) {
    case PriceType.FREE:
      price = 'Free';
      break;
    case PriceType.ONCE_OFF:
      price = `R${formatCurrencyAmount(catalogueItem.price.price)} once off`;
      break;
    case PriceType.MONTHLY:
      price = `R${formatCurrencyAmount(catalogueItem.price.price)}pm`;
      break;
  }

  const total = getTotalProducts(catalogue);
  let maximum =
    total >= catalogue.maxProductsPerOrder
      ? catalogueItem.device.quantity || 0
      : catalogueItem.device.maxQuantity ||
        catalogue.maxProductsPerOrder - (total - (catalogueItem.device.quantity || 0));

  return (
    <Field
      name={`catalogue.products[${catalogueItem.index}].device.quantity`}
      title={catalogueItem.device.title}
      price={price}
      hasLoaded={catalogueItem.price.status === DataStatus.LOADED}
      crossedOut={productStaticConfig?.priceSubtitle}
      image={catalogueItem.device.imageBase64}
      tagText={productStaticConfig?.tagText}
      badge={productStaticConfig?.badge}
      component={CarouselCard}
      min={0}
      max={maximum}
      singleSelection={
        catalogueItem.device.title === DEVICE_NAMES.VODAPAY_ECOM_GATEWAY ||
        catalogueItem.device.title === DEVICE_NAMES.TAP_ON_PHONE ||
        catalogueItem.device.title === DEVICE_NAMES.VODAPAY_PAYMENT_REQUEST
      }
      automationid={`Revamp_Choose_Solution_Carousel_Card_${convertToAutomationId(
        catalogueItem.device.title,
      )}`}
    />
  );
}
